import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import { TagCategory } from '@leedpulse-monorepo/enums';
import { v4 as uuidv4 } from 'uuid';

export default class Tag {
  private _id: string;
  private _name: string;
  private _category: TagCategory;
  private _idDomain: string | null;
  private _idSubscription: string | null;
  private _promptInstructions: string;
  private _color: string;
  private _created: Date;
  private _updated: Date;

  constructor(
    id: string,
    name: string,
    category: TagCategory,
    idDomain: string | null,
    idSubscription: string | null,
    promptInstructions: string,
    color: string,
    created: Date,
    updated: Date
  ) {
    this._id = id;
    this._name = name;
    this._category = category;
    this._idDomain = idDomain;
    this._idSubscription = idSubscription;
    this._promptInstructions = promptInstructions;
    this._color = color;
    this._created = created;
    this._updated = updated;
  }

  get id(): string {
    return this._id;
  }

  get name(): string {
    return this._name;
  }

  get category(): TagCategory {
    return this._category;
  }

  get idDomain(): string | null {
    return this._idDomain;
  }

  get idSubscription(): string | null {
    return this._idSubscription;
  }

  get promptInstructions(): string {
    return this._promptInstructions;
  }

  get color(): string {
    return this._color;
  }

  get created(): Date {
    return this._created;
  }

  get updated(): Date {
    return this._updated;
  }

  public copyWith({
    name,
    category,
    idDomain,
    idSubscription,
    promptInstructions,
    color,
  }: {
    name?: string;
    category?: TagCategory;
    idDomain?: string | null;
    idSubscription?: string | null;
    promptInstructions?: string;
    color?: string;
  }): Tag {
    return new Tag(
      this._id,
      name ?? this._name,
      category ?? this._category,
      idDomain !== undefined ? idDomain : this._idDomain,
      idSubscription !== undefined ? idSubscription : this._idSubscription,
      promptInstructions ?? this._promptInstructions,
      color ?? this._color,
      this._created,
      new Date()
    );
  }

  static generateTagId(): string {
    return `tag-${uuidv4()}`;
  }

  static newEmpty(): Tag {
    return new Tag(
      Tag.generateTagId(),
      '',
      TagCategory.custom,
      null,
      null,
      '',
      '#000000',
      new Date(),
      new Date()
    );
  }

  static fromFirestore(doc: QueryDocumentSnapshot<DocumentData, DocumentData>): Tag {
    const docData = doc.data();
    return new Tag(
      doc.id,
      docData.name,
      docData.category,
      docData.id_domain ?? null,
      docData.id_subscription ?? null,
      docData.prompt_instructions ?? '',
      docData.color ?? '#000000',
      new Date(docData.created),
      new Date(docData.updated)
    );
  }

  static fromApi(data: Record<string, unknown>): Tag {
    return new Tag(
      data['id'] as string,
      data['name'] as string,
      data['category'] as TagCategory,
      (data['id_domain'] as string | null) ?? null,
      (data['id_subscription'] as string | null) ?? null,
      (data['prompt_instructions'] as string) ?? '',
      (data['color'] as string) ?? '#000000',
      new Date(data['created'] as number),
      new Date(data['updated'] as number)
    );
  }

  toFirestorePatch(): Record<string, unknown> {
    return {
      name: this._name,
      category: this._category,
      id_domain: this._idDomain,
      id_subscription: this._idSubscription,
      prompt_instructions: this._promptInstructions,
      color: this._color,
      updated: new Date().getTime(),
    };
  }

  toFirestore(): Record<string, unknown> {
    return {
      id: this._id,
      name: this._name,
      category: this._category,
      id_domain: this._idDomain,
      id_subscription: this._idSubscription,
      prompt_instructions: this._promptInstructions,
      color: this._color,
      created: this._created.getTime(),
      updated: this._updated.getTime(),
    };
  }
}
